import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import { useCallback, useState } from "react";

interface UpdateNameModalProps {
  readonly open: boolean;
  readonly currentName: string;
  readonly onClose: () => void;
  readonly onUpdate: (newName: string) => Promise<void>;
}

function UpdateNameModal({
  open,
  currentName,
  onClose,
  onUpdate,
}: UpdateNameModalProps) {
  const [newName, setNewName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");

  const handleClose = useCallback(() => {
    setNewName("");
    setError("");
    onClose();
  }, [onClose]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setNewName(value);

      // Validate
      if (value.length === 0) {
        setError("Practice name is required");
      } else if (value.length > 255) {
        setError("Practice name must be 255 characters or less");
      } else {
        setError("");
      }
    },
    []
  );

  const handleUpdate = useCallback(async () => {
    if (!newName || error || newName === currentName) return;

    try {
      setIsUpdating(true);
      await onUpdate(newName);
      handleClose();
    } catch (err) {
      console.error("Error updating name:", err);
      // Error toast is handled by parent
    } finally {
      setIsUpdating(false);
    }
  }, [newName, error, currentName, onUpdate, handleClose]);

  const isUpdateDisabled =
    !newName || !!error || newName === currentName || isUpdating;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Practice Name</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Current Name (Read-only) */}
          <TextField
            label="Current Practice Name"
            value={currentName}
            fullWidth
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f5f5",
              },
              mb: 3, // Add margin bottom for spacing
            }}
          />

          {/* New Name (Editable) */}
          <TextField
            label="New Practice Name"
            value={newName}
            onChange={handleNameChange}
            fullWidth
            required
            error={!!error}
            helperText={error || `${newName.length}/255 characters`}
            slotProps={{
              htmlInput: {
                maxLength: 255,
              },
            }}
            autoFocus
            disabled={isUpdating}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={isUpdateDisabled}
          startIcon={isUpdating ? <CircularProgress size={20} /> : null}
        >
          {isUpdating ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UpdateNameModal;
